// NRI data model — 6 countries + hub content.
// Each country has authentic differentiation: currency, timezone/flight logistics,
// visa specifics, common referral patterns, and tone notes.

export const NRI_HUB = {
  kicker: "For NRI patients",
  lede: "Continuity across borders — from your home city, to Mohali, and back.",
  intro:
    "Every year Dr Sidhu treats patients travelling from Canada, the US, the UK, Australia, the UAE, and Saudi Arabia. The playbook is the same wherever you fly from: a real video consult with the doctor before you book a flight, imaging reviewed once (not repeated on arrival), coordinated admission at Max Mohali, and post-departure follow-up on the same WhatsApp thread you started on.",
  promises: [
    { k: "Video first", v: "A full consultation with Dr Sidhu before you commit to travel — never a coordinator, never a substitute." },
    { k: "One doctor, three continents", v: "The consultant who reviews your imaging is the consultant who does the procedure and does your follow-up. No exceptions." },
    { k: "Imaging respected", v: "Send your existing scans — we read them. You aren't asked to repeat what you've already done." },
    { k: "Transparent pricing", v: "A written estimate in your currency before you board a plane. No surprises on arrival." },
    { k: "Coordinated admission", v: "Max Super Speciality Hospital, Mohali. Admission, pre-op investigations, and discharge planning arranged before you land." },
    { k: "Post-return follow-up", v: "Six-week and six-month reviews on WhatsApp video from your home country — no return trip needed unless it should be." },
  ],
};

export const COUNTRIES = [
  {
    slug: "canada",
    name: "Canada",
    flag: "🇨🇦",
    currency: "CAD",
    tone: "conservative-clinical",
    cities: ["Toronto (GTA & Brampton)", "Vancouver & Surrey", "Calgary", "Edmonton", "Montreal"],
    lede: "Punjab's largest overseas community. Half of Dr Sidhu's NRI patients come from a Canadian postcode.",
    whyTravel:
      "OHIP and provincial coverage doesn't include many uterine-artery embolisations, PAEs, or timely private IR review. Wait-lists for elective vascular work stretch across seasons. Patients — often middle-aged Punjabi Canadians with strong Mohali family links — travel to see the same doctor across a single visit rather than a fragmented year of referrals.",
    logistics: {
      "Direct flights": "Air Canada and Air India — Toronto and Vancouver → Delhi (14–16h). Onward: 40-min flight or 4-hour drive Delhi → Chandigarh/Mohali.",
      "Ideal stay": "10–14 days for most day-case procedures with post-op observation. Longer for complex vascular or oncology work.",
      "Medical visa": "e-Medical Visa is straightforward for Canadian passport holders — apply after we send the treatment estimate.",
      "Best months": "October–March suit most patients; avoid mid-June to mid-September if heat is a concern.",
    },
    commonConditions: ["uterine-fibroids", "peripheral-artery-disease", "prostate-artery-embolisation", "varicose-veins", "aortic-aneurysm"],
    story: {
      quote: "Two OB-GYNs in Brampton offered hysterectomy. Dr Sidhu offered UAE. Six months later my periods are half as heavy and the fibroid is 60% smaller — and I still have my uterus.",
      attribution: "Patient, GTA, Ontario",
    },
    localNote: "Full clinical letters and imaging discs are provided for your Canadian GP or specialist. Follow-up scans in Canada; interpretation done here.",
  },
  {
    slug: "usa",
    name: "United States",
    flag: "🇺🇸",
    currency: "USD",
    tone: "precision-cost",
    cities: ["New York & New Jersey", "SF Bay Area", "Chicago", "Houston & Dallas", "Atlanta"],
    lede: "For US patients, the trade-off is often clarity, cost, and time — not clinical quality.",
    whyTravel:
      "Even with strong US insurance, elective procedures — PAE, UAE, varicose treatment, prostate artery embolisation — carry surprise-billing risk, high deductibles, and referral choreography that can stretch to months. Patients who already visit family in Punjab often prefer a single trip with a written estimate, a real IR consultant, and predictable timelines.",
    logistics: {
      "Direct flights": "Air India and United — Newark, JFK, San Francisco, Chicago → Delhi (14–16h). Onward: 40-min flight or 4-hour drive to Mohali.",
      "Ideal stay": "10–14 days typical; 3 weeks for complex oncology IR.",
      "Medical visa": "e-Medical Visa for US passport holders is fast. A medical attendant visa can accompany a family member.",
      "Insurance letters": "We provide detailed procedure and CPT-equivalent documentation for out-of-network reimbursement claims (results vary by carrier).",
    },
    commonConditions: ["uterine-fibroids", "prostate-artery-embolisation", "liver-cancer-tace", "y-90-radioembolisation", "varicose-veins"],
    story: {
      quote: "The UAE quote from a Manhattan hospital was $32,000 out-of-pocket after insurance. Mohali was a fraction of that, done by an IR consultant I actually spoke to on video — twice — before I flew.",
      attribution: "Patient, New Jersey",
    },
    localNote: "Second-opinion imaging review is offered ahead of travel, in writing, at a fixed fee. Refundable against treatment if you proceed.",
  },
  {
    slug: "united-kingdom",
    name: "United Kingdom",
    flag: "🇬🇧",
    currency: "GBP",
    tone: "warm-british",
    cities: ["London", "Birmingham & West Midlands", "Manchester & Greater North", "Glasgow & Edinburgh", "Southampton"],
    lede: "NHS waits push electives private. British Indian patients often make the maths — and the family visit — work together.",
    whyTravel:
      "NHS elective wait-lists for varicose, fibroid embolisation, PAE, and non-urgent vascular review can stretch six months or more. UK private care is excellent but expensive. For families with existing Punjab connections, treatment in Mohali — with a UK-style consultation-first model — is often the pragmatic choice.",
    logistics: {
      "Direct flights": "Air India, British Airways, Virgin Atlantic — Heathrow / Gatwick / Birmingham → Delhi (9h). Onward: 40-min flight or 4-hour drive to Mohali.",
      "Ideal stay": "7–14 days for most procedures. Combine with family time.",
      "Medical visa": "e-Medical Visa for UK passport holders; a medical attendant visa can accompany.",
      "GP letters": "Full clinical letters and imaging DVDs provided for your NHS GP, and follow-up done by WhatsApp video.",
    },
    commonConditions: ["uterine-fibroids", "varicose-veins", "prostate-artery-embolisation", "peripheral-artery-disease", "carotid-stenting"],
    story: {
      quote: "The Birmingham NHS wait for uterine fibroid embolisation was 11 months. Dr Sidhu had me on video within a week, in a hospital gown within three. Sensible, calm, no upsell.",
      attribution: "Patient, West Midlands",
    },
    localNote: "Familiar with BSIR guidance and RCR referral standards; case letters are written to be readable by UK colleagues.",
  },
  {
    slug: "australia",
    name: "Australia",
    flag: "🇦🇺",
    currency: "AUD",
    tone: "practical-direct",
    cities: ["Sydney", "Melbourne", "Brisbane", "Perth", "Adelaide"],
    lede: "Long flight, longer Medicare queues. Australians of Punjabi origin often align a Mohali visit with family.",
    whyTravel:
      "Medicare-covered IR access is variable by state, and elective interventions carry meaningful private gap payments. Patients who already visit family in Punjab typically consolidate the visit with a real consultation, one procedure, and a post-return follow-up loop.",
    logistics: {
      "Direct flights": "Singapore Airlines and Qantas via Singapore, or Air India via Sydney/Melbourne → Delhi (roughly 16–18h with a stop). Onward to Mohali as above.",
      "Ideal stay": "12–16 days; a longer buffer helps recover from jet-lag before procedure day.",
      "Medical visa": "e-Medical Visa for Australian passports; medical attendant visa available.",
      "Best months": "September–March; consider a rest day post-arrival before consult.",
    },
    commonConditions: ["diabetic-foot-angioplasty", "uterine-fibroids", "peripheral-artery-disease", "kidney-cancer-ablation", "varicocele"],
    story: {
      quote: "Melbourne to Delhi felt long, but the first day at Max Mohali felt shorter than a Melbourne outpatient wait. We got the answer, the procedure, and a plan — all in one week.",
      attribution: "Patient, Victoria",
    },
    localNote: "We schedule the pre-arrival video consult for your evening (AEDT) — 3–4pm IST — so you don't have to take a work day off.",
  },
  {
    slug: "uae",
    name: "United Arab Emirates",
    flag: "🇦🇪",
    currency: "AED",
    tone: "efficient-professional",
    cities: ["Dubai", "Abu Dhabi", "Sharjah", "Ras Al Khaimah", "Al Ain"],
    lede: "3.5 hours to Delhi. UAE-based Indians often make it a single-weekend consult and a single-week treatment.",
    whyTravel:
      "UAE healthcare is world-class but expensive, especially for elective embolisation and IR procedures where local availability and pricing vary widely. The 3.5-hour proximity to Delhi and the strong family links to Punjab make short, focused Mohali visits highly practical.",
    logistics: {
      "Direct flights": "Emirates, Etihad, Air India Express, IndiGo — Dubai / Abu Dhabi / Sharjah → Delhi (3.5h). Onward to Chandigarh (40-min flight) or 4-hour drive.",
      "Ideal stay": "5–10 days for day-case procedures.",
      "Medical visa": "e-Medical Visa is quick for UAE residents; the process is used to this route.",
      "Employer letters": "We provide fitness-to-return-to-work letters routinely required by UAE employers.",
    },
    commonConditions: ["prostate-artery-embolisation", "uterine-fibroids", "varicocele", "varicose-veins", "peripheral-artery-disease"],
    story: {
      quote: "Left Dubai on Thursday evening, embolised on Sunday, back at my desk in JLT by the next Monday — with a follow-up scheduled from home.",
      attribution: "Patient, Dubai Marina",
    },
    localNote: "Video consults scheduled to fit UAE working hours (GST +4) — early morning IST is your evening.",
  },
  {
    slug: "saudi-arabia",
    name: "Saudi Arabia",
    flag: "🇸🇦",
    currency: "SAR",
    tone: "efficient-professional",
    cities: ["Riyadh", "Jeddah", "Dammam & Khobar", "Al Madinah", "Al Ahsa"],
    lede: "For Indians on Saudi contracts, a short trip home for planned care is often the cleanest answer.",
    whyTravel:
      "Employer insurance in the Kingdom varies widely for interventional and elective vascular procedures, and second opinions are often difficult to arrange locally. Combining a planned Mohali visit with family time in Punjab is the model that most Saudi-based Indian patients choose.",
    logistics: {
      "Direct flights": "Saudia and Air India — Riyadh / Jeddah / Dammam → Delhi (roughly 6h). Onward to Chandigarh (40-min flight).",
      "Ideal stay": "7–12 days, extending as needed to combine with family visit.",
      "Medical visa": "e-Medical Visa is standard; employer no-objection letters occasionally requested for extended leave.",
      "Language & culture": "Practice familiar with common expat patient concerns — Hijri scheduling notes, prayer-time consideration on ward rounds.",
    },
    commonConditions: ["dialysis-access", "peripheral-artery-disease", "prostate-artery-embolisation", "uterine-fibroids", "gi-bleed-embolisation"],
    story: {
      quote: "In Riyadh, my fistula problems were seen as 'watch and wait'. In Mohali, they were declotted and the underlying stenosis dilated on the same admission. I was back on dialysis in Al Ahsa within a week.",
      attribution: "Patient, Al Ahsa",
    },
    localNote: "Familiar routes back to your Saudi provider — clinical letters and imaging discs are hospital-ready.",
  },
];

export const getCountry = (slug) => COUNTRIES.find((c) => c.slug === slug);
